import React, {
    ReactElement, useEffect, useMemo, useState, MouseEvent,
} from "react";
import {
    createEditor, Descendant, Editor as SlateEditor, Node, Transforms,
} from "slate";
import { withHistory } from "slate-history";
import {
    Editable, ReactEditor, Slate, withReact,
} from "slate-react";
import useUploadAttachment from "../../upload";

import { ElementType } from "./blocks";
import { BlockDeserializeSettings, StateElement, TextElement } from "./blocks/base";
import Styles from "./Editor.module.scss";
import EmojiBar from "./emoji/EmojiBar";
import KeyboardEventHandler from "./keyboard-event-handler";
import {
    BlockElement, deserialize, deserializeFromHtml, Leaf, serialize,
} from "./Leaf";
import MentionBar, { MentionedUser } from "./MentionBar";
import { clearStyles, onDOMBeforeInput, Toolbar } from "./Toolbar";

export interface Props {
    text?: string;
    id?: string;
    onChange?: (html: string) => void;
    isReadOnly?: boolean;
    editorClassName?: string;
    className?: string;
    onCommandEnterPressed?: (html: string) => void;
    mentionSourceId?: string;
    placeholder?: string;
    isScrollDisabled?: boolean;
    onFilesPasted?: (file: File[]) => Promise<void>;
    shouldAllowAttachments?: boolean;
    shouldFocus?: boolean;
    onUsersSelected?: (user: MentionedUser) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onKeyUpArrow?: () => void;
    onLinkClicked?: (event: MouseEvent) => void;
    linkAttributes?: (url: string | undefined) => Partial<Record<keyof HTMLLinkElement, unknown>>;

}

export const DEFAULT_TEXT = "<p></p>";
const DEFAULT = deserializeFromHtml("<p></p>", {});
const RESET_VALUE = { type: ElementType.PARAGRAPH, children: [] };

export interface EditorInstance {
    focus: () => void;
    focusEnd: () => void;
    clear: () => void;
}
const Editor = React.forwardRef<EditorInstance, Props>(({
    text, isReadOnly, editorClassName, className, onCommandEnterPressed, onChange, onFilesPasted, shouldAllowAttachments,
    mentionSourceId, placeholder, isScrollDisabled, shouldFocus, onUsersSelected, onLinkClicked, linkAttributes,
    onFocus, onBlur, onKeyUpArrow,
}: Props, ref: React.ForwardedRef<EditorInstance>): ReactElement => {
    const deserializeSettings: BlockDeserializeSettings = {
        onLinkClicked,
        linkAttributes,
    };
    const [value, setValue] = useState<Descendant[]>(
        () => (text ? deserializeFromHtml(text ?? "", deserializeSettings) : DEFAULT),
    );
    const [editor] = useState(() => {
        const currentEditor = withHistory(withReact(createEditor() as ReactEditor));
        const { isVoid } = currentEditor;
        currentEditor.isVoid = element => (element.type === ElementType.IMAGE ? true : isVoid(element));
        return currentEditor;
    });
    const keyboardEvents = useMemo(() => new KeyboardEventHandler(), []);
    const [lastChanged, setLastChanged] = useState<string>();

    const [uploadAttachment] = useUploadAttachment();
    const clear = () => {
        for (let i = 0; editor.children.length > 0 && i < 10; i++) {
            Transforms.removeNodes(editor, {
                at: {
                    offset: 0,
                    anchor: SlateEditor.start(editor, []),
                    focus: SlateEditor.end(editor, []),
                },
                hanging: false,
                voids: true,
                mode: "lowest",
            });
        }
    };
    const addDefault = () => {
        Transforms.setSelection(editor, {
            anchor: SlateEditor.start(editor, []),
            focus: SlateEditor.end(editor, []),
        });
        Transforms.delete(editor);
        Transforms.setNodes(editor, RESET_VALUE, {
            at: {
                anchor: SlateEditor.start(editor, []),
                focus: SlateEditor.end(editor, []),
            },
        });
    };

    useEffect(() => {
        if (text === "" || text === lastChanged) {
            return;
        }

        clear();

        const deserialized = text ? deserializeFromHtml(text ?? "", deserializeSettings) : DEFAULT;
        Transforms.insertNodes(editor, deserialized);

        if (editor.children.length > deserialized.length) {
            Transforms.removeNodes(editor, {
                at: {
                    anchor: SlateEditor.start(editor, []),
                    focus: SlateEditor.start(editor, []),
                },
            });
        }

        Transforms.select(editor, {
            anchor: SlateEditor.start(editor, []),
            focus: SlateEditor.start(editor, []),
        });

        setValue(deserialized);
    }, [text]);
    useEffect(() => {
        if (shouldFocus) {
            ReactEditor.focus(editor);
            Transforms.select(editor, {
                anchor: SlateEditor.start(editor, []),
                focus: SlateEditor.start(editor, []),
            });
        }
    }, [editor]);

    editor.insertData = async data => {
        const types = new Set(data.types);
        if (types.has("text/html")) {
            const html = data.getData("text/html");
            if (html) {
                const document = new window.DOMParser().parseFromString(html, "text/html");
                const items = deserialize(document.body, {

                });
                if (Array.isArray(items)) {
                    items.forEach(row => {
                        editor.insertNode(row);
                    });
                } else {
                    editor.insertNode(items as Node);
                }
            }
        } else if (types.has("text/plain")) {
            const copiedText = data.getData("text/plain");
            if (copiedText) {
                editor.insertNode({ text: copiedText } as TextElement);
            }
        } else if (types.has("Files") && (onFilesPasted || shouldAllowAttachments)) {
            const files = [];
            for (let i = 0; i < data.files.length; i++) {
                const file = data.files.item(i);
                if (file) {
                    if (onFilesPasted) {
                        files.push(file);
                    }
                    if (shouldAllowAttachments) {
                        (async () => {
                            const uploaded = await uploadAttachment(file);
                            // editor.insertNode({
                            //     type: ElementType.PARAGRAPH,
                            //     children: [{
                            //         [ElementType.IMAGE]: {
                            //             src: uploaded.url,
                            //             altText: uploaded.name,
                            //         },
                            //         text: "",
                            //     } as unknown as TextElement],
                            // });
                            if (uploaded.mimeType.startsWith("video")) {
                                editor.insertNode({
                                    type: ElementType.VIDEO,
                                    [ElementType.VIDEO]: {
                                        src: uploaded.url,
                                        mimeType: uploaded.mimeType,
                                    },
                                    children: [{
                                        text: "",
                                    } as TextElement],
                                });
                            } else {
                                editor.insertNode({
                                    type: ElementType.IMAGE,
                                    [ElementType.IMAGE]: {
                                        src: uploaded.url,
                                        altText: uploaded.name,
                                    },
                                    children: [{
                                        text: "",
                                    } as TextElement],
                                });
                            }

                            editor.insertNode({
                                type: ElementType.PARAGRAPH,
                                children: [{
                                    text: "",
                                } as unknown as TextElement],
                            });
                        })();
                    }
                }
            }
            onFilesPasted?.(files);
        }
    };
    if (ref) {
        // eslint-disable-next-line no-param-reassign
        (ref as React.MutableRefObject<EditorInstance>).current = {
            focus: () => {
                ReactEditor.focus(editor);
                Transforms.setSelection(editor, {
                    anchor: SlateEditor.start(editor, []),
                    focus: SlateEditor.end(editor, []),
                });
            },
            focusEnd: () => {
                ReactEditor.focus(editor);
                Transforms.setSelection(editor, {
                    anchor: SlateEditor.end(editor, []),
                    focus: SlateEditor.end(editor, []),
                });
            },
            clear: () => {
                addDefault();
            },
        };
    }

    const handleChange = (descendents: Descendant[]) => {
        setValue(descendents);
        if (isReadOnly) {
            return;
        }
        const serialized = serialize(descendents);
        setLastChanged(serialized);
        if (lastChanged == null || lastChanged === serialized) {
            return;
        }
        onChange?.(serialized);
    };

    const onKeyDown = (pressed: React.KeyboardEvent) => {
        if (pressed.key === "ArrowUp" && editor.selection?.focus.offset === 0 && editor.selection?.anchor.offset === 0) {
            pressed.preventDefault();
            onKeyUpArrow?.();
        }
        if (pressed.key === "Enter" && pressed.shiftKey) {
            pressed.preventDefault();
            editor.insertSoftBreak();
            return;
        }

        if (pressed.key === "Enter" && (pressed.ctrlKey || pressed.metaKey) && onCommandEnterPressed) {
            pressed.preventDefault();
            const serialized = serialize(value);
            onCommandEnterPressed(serialized);
            addDefault();
            clearStyles(editor);

            ReactEditor.focus(editor);
        }

        const keys = [];
        if (pressed.ctrlKey) {
            keys.push("ctrl");
        }
        if (pressed.metaKey) {
            keys.push("cmd");
        }

        keys.push(pressed.key);
        keyboardEvents.emit(keys.join("+"), pressed);
        if (pressed.key === "Escape" && !pressed.isPropagationStopped()) {
            ReactEditor.blur(editor);
            pressed.stopPropagation();
        }
    };
    const onKeyPress = (event: React.KeyboardEvent) => {
        keyboardEvents.emitPressed(event.key, event);
    };

    const onKeyUp = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
            ReactEditor.focus(editor);
        }
        keyboardEvents.emitKeyUp(event.key, event);
    };

    const onClickEditor = (evt: React.MouseEvent<HTMLDivElement>) => {
        evt.stopPropagation();
    };
    const onClickWrapper = () => {
        if (ReactEditor.isFocused(editor)) {
            return;
        }
        ReactEditor.focus(editor);

        Transforms.select(editor, {
            anchor: SlateEditor.end(editor, []),
            focus: SlateEditor.end(editor, []),
        });
    };
    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className={`${Styles.editor} text-gray-800 ${className}`} onClick={onClickWrapper}>

            <Slate editor={editor} initialValue={value} onChange={handleChange}>
                {!isReadOnly && (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <div onClick={onClickEditor}>
                        <Toolbar keyboardEvents={keyboardEvents} />
                        <EmojiBar
                            editor={editor}
                            keyboardEvents={keyboardEvents}
                        />
                        {mentionSourceId && (
                            <MentionBar
                                editor={editor}
                                keyboardEvents={keyboardEvents}
                                sourceId={mentionSourceId}
                                onChange={onUsersSelected}
                            />
                        )}
                    </div>
                )}

                <Editable
                    onClick={onClickEditor}
                    readOnly={isReadOnly}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderLeaf={props => (
                        <Leaf
                            attributes={props.attributes as React.HTMLProps<HTMLSpanElement>}
                            leaf={props.leaf}
                            editorSettings={{
                                isReadOnly: isReadOnly ?? true,
                                onRemove: () => {
                                    const path = ReactEditor.findPath(editor, props.leaf);
                                    Transforms.removeNodes(editor, { at: path });
                                },
                                onUpdate: (element: StateElement) => {
                                    const path = ReactEditor.findPath(editor, props.leaf);
                                    Transforms.setNodes(editor, element, { at: path });
                                },
                            }}
                        >
                            {props.children}
                        </Leaf>
                    )}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderElement={props => (
                        <BlockElement
                            {...props}
                            editorSettings={{
                                isReadOnly: isReadOnly ?? true,
                                onRemove: () => {
                                    const path = ReactEditor.findPath(editor, props.element);
                                    Transforms.removeNodes(editor, { at: path });
                                },
                                onUpdate: (element: StateElement) => {
                                    const path = ReactEditor.findPath(editor, props.element);
                                    Transforms.setNodes(editor, element, { at: path });
                                },
                            }}
                        />
                    )}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={placeholder ?? "Once upon a time..."}
                    onDOMBeforeInput={val => onDOMBeforeInput(editor, val)}
                    className={`w-100 h-100 ${editorClassName}`}
                    onKeyDown={onKeyDown}
                    onKeyPress={onKeyPress}
                    onKeyUp={onKeyUp}
                    scrollSelectionIntoView={isScrollDisabled ? () => { /* do nothing */ } : undefined}
                />
            </Slate>
        </div>
    );
});

Editor.defaultProps = {
    text: "",
    id: undefined,

    onChange: () => { /* Do nothing by default */ },
    editorClassName: "",
    className: "",
    isReadOnly: false,
    onCommandEnterPressed: undefined,
    mentionSourceId: undefined,
    shouldAllowAttachments: false,
    shouldFocus: true,
    onFocus: undefined,
    onBlur: undefined,
} as Props;

export default Editor;
